import { useTranslation } from 'react-i18next';
import { Eye, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { useIsImpersonating } from '@/features/auth/useIsImpersonating.js';

/**
 * Pure presentational read-only impersonation banner. Kept store-free so it can
 * render in tests without the zustand store (whose bundled use-sync-external-store
 * shim binds to a duplicate React copy and crashes @testing-library rendering).
 */
export function ImpersonationBannerView({
  username,
  onExit,
}: {
  username: string;
  onExit: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <div
      role="status"
      className="sticky top-0 z-[60] flex w-full items-center justify-center gap-3 border-b border-[var(--border-strong)] bg-[var(--warning,#b45309)] px-4 py-2 text-[13px] font-semibold text-white"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <span className="truncate">
        {t('impersonation.viewingAs')}{' '}
        <span className="font-bold">{username}</span>
        {' · '}
        {t('impersonation.staff')}
        {' · '}
        {t('impersonation.readOnly')}
      </span>
      <button
        type="button"
        onClick={onExit}
        className="ml-1 inline-flex shrink-0 items-center gap-1.5 rounded-md bg-black/25 px-2.5 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-black/40"
      >
        <LogOut className="size-3.5" aria-hidden />
        {t('impersonation.exit')}
      </button>
    </div>
  );
}

/**
 * Persistent read-only banner shown on every page while a staff member is
 * viewing the app in the impersonation ("login as member") session. The server
 * is the real read-only gate (every mutating handler rejects an impersonation
 * token); this banner is the visible reminder + the exit affordance.
 */
export function ImpersonationBanner() {
  const isImpersonating = useIsImpersonating();
  const subject = useAuthStore((s) => s.subject);
  const clear = useAuthStore((s) => s.clear);

  if (!isImpersonating) return null;

  const username = subject?.displayName ?? subject?.id ?? '';

  const onExit = () => {
    clear();
    // Best-effort close the impersonation tab. window.close() only succeeds for
    // script-opened tabs; if the browser refuses, fall back to the login page so
    // the staff session never lingers on a cleared store.
    window.close();
    if (!window.closed) {
      window.location.assign('/login');
    }
  };

  return <ImpersonationBannerView username={username} onExit={onExit} />;
}
